/**
 * SupplierDialog Component
 *
 * Dialog for creating and editing suppliers.
 * Form fields: Name (required), Contact Name, Email, Phone, Website, Lead Time Days, Notes
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 1: Suppliers Management
 */

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useSuppliers } from '@/hooks/useSuppliers'
import { toast } from 'sonner'
import { Building2, Loader2 } from 'lucide-react'

/**
 * SupplierDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onOpenChange - Callback when dialog state changes
 * @param {Object|null} props.supplier - Supplier to edit (null for create mode)
 * @param {Function} props.onSuccess - Callback on successful create/update
 */
export default function SupplierDialog({ open, onOpenChange, supplier, onSuccess }) {
  const { createSupplier, updateSupplier } = useSuppliers()

  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    lead_time_days: 7,
    notes: '',
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditMode = !!supplier

  // Reset form when dialog opens/closes or supplier changes
  useEffect(() => {
    if (open) {
      if (supplier) {
        // Edit mode - populate form with supplier data
        setFormData({
          name: supplier.name || '',
          contact_name: supplier.contact_name || '',
          email: supplier.email || '',
          phone: supplier.phone || '',
          website: supplier.website || '',
          lead_time_days: supplier.lead_time_days ?? 7,
          notes: supplier.notes || '',
        })
      } else {
        // Create mode - reset form
        setFormData({
          name: '',
          contact_name: '',
          email: '',
          phone: '',
          website: '',
          lead_time_days: 7,
          notes: '',
        })
      }
      setErrors({})
    }
  }, [open, supplier])

  const validateForm = () => {
    const newErrors = {}

    // Name is required
    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required'
    }

    // Email format validation (if provided)
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Lead time must be a non-negative number
    if (formData.lead_time_days !== '' && formData.lead_time_days !== null) {
      const leadTime = Number(formData.lead_time_days)
      if (isNaN(leadTime) || leadTime < 0) {
        newErrors.lead_time_days = 'Lead time must be a positive number'
      }
    }

    // Website URL validation (if provided)
    if (formData.website.trim()) {
      try {
        new URL(formData.website)
      } catch {
        newErrors.website = 'Please enter a valid URL (e.g., https://example.com)'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const supplierData = {
        name: formData.name.trim(),
        contact_name: formData.contact_name.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        website: formData.website.trim() || null,
        lead_time_days: formData.lead_time_days !== '' ? Number(formData.lead_time_days) : 7,
        notes: formData.notes.trim() || null,
      }

      if (isEditMode) {
        await updateSupplier(supplier.id, supplierData)
        toast.success('Supplier updated', {
          description: `${supplierData.name} has been updated.`,
        })
        onSuccess?.()
      } else {
        const created = await createSupplier(supplierData)
        toast.success('Supplier created', {
          description: `${supplierData.name} has been added.`,
        })
        onSuccess?.(created)
      }
    } catch (err) {
      console.error('Error saving supplier:', err)
      toast.error('Error saving supplier', {
        description: err.message,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isEditMode ? 'Edit Supplier' : 'Add Supplier'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Supplier Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Supplier Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter supplier name"
              className={errors.name ? 'border-red-500' : ''}
              autoFocus
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>

          {/* Contact Name */}
          <div className="space-y-2">
            <Label htmlFor="contact_name">Contact Name</Label>
            <Input
              id="contact_name"
              value={formData.contact_name}
              onChange={(e) => handleInputChange('contact_name', e.target.value)}
              placeholder="Primary contact person"
            />
          </div>

          {/* Email and Phone - Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="supplier@example.com"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+44 20 1234 5678"
              />
            </div>
          </div>

          {/* Website and Lead Time - Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://example.com"
                className={errors.website ? 'border-red-500' : ''}
              />
              {errors.website && <p className="text-red-500 text-sm">{errors.website}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead_time_days">Lead Time (days)</Label>
              <Input
                id="lead_time_days"
                type="number"
                min="0"
                value={formData.lead_time_days}
                onChange={(e) => handleInputChange('lead_time_days', e.target.value)}
                placeholder="7"
                className={errors.lead_time_days ? 'border-red-500' : ''}
              />
              {errors.lead_time_days && (
                <p className="text-red-500 text-sm">{errors.lead_time_days}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes about this supplier..."
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={isEditMode ? 'default' : 'create'}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Supplier' : 'Add Supplier'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
